import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import type { ReactNode } from 'react'

// App startup milestones — native splash dismisses at first_paint_ready (see App.tsx)
export type LoadingMilestone = 
  | 'app_started'           // 5% - App initialization starting
  | 'contexts_initializing' // 15% - Context providers initializing
  | 'contexts_ready'        // 25% - All contexts initialized
  | 'auth_starting'         // 35% - Firebase auth initialization starting
  | 'auth_initialized'      // 50% - Firebase auth ready
  | 'route_loading'         // 65% - Page component code-splitting and loading
  | 'route_loaded'          // 80% - Page component mounted
  | 'first_paint_ready'     // 100% - DOM ready; native splash may hide

const MILESTONE_PROGRESS: Record<LoadingMilestone, number> = {
  app_started: 5,
  contexts_initializing: 15,
  contexts_ready: 25,
  auth_starting: 35,
  auth_initialized: 50,
  route_loading: 65,
  route_loaded: 80,
  first_paint_ready: 100,
}

interface AppReadyContextType {
  contentReady: boolean
  signalContentReady: () => void
  resetContentReady: () => void
  reachMilestone: (milestone: LoadingMilestone) => void
}

const AppReadyContext = createContext<AppReadyContextType | undefined>(undefined)

export function AppReadyProvider({ children }: { children: ReactNode }) {
  const [contentReady, setContentReady] = useState(false)
  const reachedMilestonesRef = useRef<Set<LoadingMilestone>>(new Set())
  
  const reachMilestone = useCallback((milestone: LoadingMilestone) => {
    // Check if already reached
    if (reachedMilestonesRef.current.has(milestone)) return
    
    // Add to reached milestones
    reachedMilestonesRef.current.add(milestone)
    
    const progress = MILESTONE_PROGRESS[milestone]
    console.log(`[AppReady] Milestone reached: ${milestone} (${progress}%)`)
    
    // Signal ready when first paint is ready (native splash can hide)
    if (milestone === 'first_paint_ready') {
      setContentReady(true)
    }
  }, [])
  
  const signalContentReady = useCallback(() => {
    console.log('[AppReadyContext] Content ready signal received (first paint ready)')
    reachMilestone('first_paint_ready')
  }, [reachMilestone])
  
  // Auto-complete if first paint never signaled (non-feed cold start, etc.)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!reachedMilestonesRef.current.has('first_paint_ready')) {
        console.log('[AppReadyContext] Timeout reached - forcing first paint ready (UI too slow to render)')
        reachMilestone('first_paint_ready')
      }
    }, 2000) // 2 second timeout for first paint
    
    return () => clearTimeout(timeout)
  }, [reachMilestone])
  
  const resetContentReady = useCallback(() => {
    console.log('[AppReadyContext] Content ready reset')
    setContentReady(false)
    reachedMilestonesRef.current = new Set()
  }, [])
  
  return (
    <AppReadyContext.Provider value={{ contentReady, signalContentReady, resetContentReady, reachMilestone }}>
      {children}
    </AppReadyContext.Provider>
  )
}

export function useAppReady() {
  const context = useContext(AppReadyContext)
  if (context === undefined) {
    throw new Error('useAppReady must be used within an AppReadyProvider')
  }
  return context
}
